import {
    ApiCommerceChainClaimReqSchema,
    ApiCommerceChainClaimResSchema,
    ApiCommerceOrderCreateReqSchema,
    ApiCommerceOrderCreateResSchema,
    ApiCommerceOrderGetResSchema,
    ApiCommercePresentationCreateReqSchema,
    ApiCommercePresentationCreateResSchema,
    ApiCommerceSnapshotResSchema,
    type ApiCommerceChainJourney,
    type ApiCommerceErrorCode,
    type ApiCommerceOffer,
    type ApiCommerceOrder,
    type ApiCommercePresentationDirective,
    type ApiCommerceSnapshot,
    type ApiCommerceStandaloneOfferV2,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { usePostHog } from "@posthog/react";
import { useCallback, useEffect, useRef } from "react";
import { authenticatedApiCall } from "../helpers/api";
import { captureClientAnalytics } from "../helpers/analytics";
import { telegramStarsPrice } from "../helpers/commerce";
import {
    commerceOrderClientAction,
    commerceOrderPaymentWasAccepted,
    shouldPollAfterInvoiceStatus,
} from "../helpers/commerceCheckout";
import {
    openTelegramInvoice,
    type TelegramInvoiceStatus,
} from "../helpers/telegram";
import i18n from "../i18n/i18n";
import { preloadSounds } from "../helpers/audio";
import {
    COMMERCE_REWARD_SOUNDS,
    enqueueCommerceRewardCollection,
} from "../helpers/commerceRewardCollection";

export interface StandaloneCheckoutContext {
    termsVersion: string;
    refresh: () => Promise<unknown>;
}

type CheckoutOffer = Pick<
    ApiCommerceOffer,
    | "offerId"
    | "offerCode"
    | "offerRevisionId"
    | "version"
    | "prices"
    | "experiment"
    | "presentation"
> &
    Pick<ApiCommerceStandaloneOfferV2, "rewards">;

const ORDER_POLL_DELAYS_MS = [0, 500, 1_000, 2_000, 3_000, 5_000] as const;

export class CommerceApiError extends Error {
    readonly code: ApiCommerceErrorCode;

    constructor(code: ApiCommerceErrorCode) {
        super(i18n.t("commerce.errors.request", { code }));
        this.name = "CommerceApiError";
        this.code = code;
    }
}

function commerceErrorMessage(error: unknown): string {
    return error instanceof CommerceApiError
        ? error.message
        : i18n.t("commerce.errors.unknown");
}

async function retryTransportOnce<T>(call: () => Promise<T>): Promise<T> {
    try {
        return await call();
    } catch {
        return call();
    }
}

function abortableDelay(delayMs: number, signal: AbortSignal): Promise<void> {
    if (signal.aborted) return Promise.reject(signal.reason);
    if (delayMs === 0) return Promise.resolve();
    return new Promise((resolve, reject) => {
        const handleAbort = () => {
            window.clearTimeout(timerId);
            reject(signal.reason);
        };
        const timerId = window.setTimeout(() => {
            signal.removeEventListener("abort", handleAbort);
            resolve();
        }, delayMs);
        signal.addEventListener("abort", handleAbort, { once: true });
    });
}

function presentationRequest(directive: ApiCommercePresentationDirective) {
    const base = {
        requestId: crypto.randomUUID(),
        presentationId: directive.presentationId,
        targetKind: directive.targetKind,
        trigger: directive.trigger,
    };
    return ApiCommercePresentationCreateReqSchema.parse(
        directive.targetKind === "CHAIN_JOURNEY"
            ? {
                  ...base,
                  chainJourneyId: directive.chainJourney.journeyId,
              }
            : {
                  ...base,
                  offerRevisionId: directive.offer.offerRevisionId,
              },
    );
}

async function readOrder(
    orderId: string,
    signal: AbortSignal,
): Promise<ApiCommerceOrder> {
    const query = new URLSearchParams({ orderId });
    const { body } = await authenticatedApiCall(
        `/api/v1/commerce/orders?${query.toString()}`,
        ApiCommerceOrderGetResSchema,
        { signal },
    );
    if (body.status === "ERROR") throw new CommerceApiError(body.code);
    return body.order;
}

async function waitForOrderSettlement(
    orderId: string,
    signal: AbortSignal,
): Promise<ApiCommerceOrder> {
    let latest: ApiCommerceOrder | null = null;
    for (const delayMs of ORDER_POLL_DELAYS_MS) {
        await abortableDelay(delayMs, signal);
        latest = await retryTransportOnce(() => readOrder(orderId, signal));
        const action = commerceOrderClientAction(latest.status);
        if (action === "SUCCEEDED" || action === "FAILED") {
            return latest;
        }
    }
    if (!latest) throw new Error("Commerce order polling produced no result");
    return latest;
}

export function useCommerce(
    enabled: boolean,
    { autoLoad = true }: { autoLoad?: boolean } = {},
) {
    const posthog = usePostHog();
    const { state, setSlice: setCommerceSlice } = useSlice("commerceSlice");
    const { setSlice: setSlotMachineSlice } = useSlice("slotMachineSlice");
    const { setSlice: setGnomeSlice } = useSlice("gnomeCustomizationSlice");
    const { setSlice: setWheelSlice } = useSlice("wheelSlice");
    const { state: authState } = useSlice("authSlice");
    const snapshotRequestRef = useRef<AbortController | null>(null);
    const checkoutRequestRef = useRef<AbortController | null>(null);
    const presentationRequestsRef = useRef(new Map<string, string>());
    const mountedRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            snapshotRequestRef.current?.abort();
            if (checkoutRequestRef.current) {
                checkoutRequestRef.current.abort();
                checkoutRequestRef.current = null;
                // Leaving the route releases only the client lock. The server
                // order remains resumable and keeps its authoritative status.
                setCommerceSlice({
                    mutationStatus: "idle",
                    checkoutStatus: "idle",
                });
            }
        };
    }, [setCommerceSlice, authState?.userId, enabled]);

    const refresh = useCallback(async () => {
        if (!enabled) return null;

        snapshotRequestRef.current?.abort();
        const controller = new AbortController();
        snapshotRequestRef.current = controller;
        setCommerceSlice({ loadStatus: "loading", error: null });

        try {
            const { body } = await authenticatedApiCall(
                "/api/v1/commerce",
                ApiCommerceSnapshotResSchema,
                { signal: controller.signal },
            );
            if (body.status === "ERROR") {
                if (body.code === "FEATURE_DISABLED") {
                    if (mountedRef.current && !controller.signal.aborted) {
                        setCommerceSlice({
                            loadStatus: "disabled",
                            snapshot: null,
                            activePresentation: null,
                            error: null,
                        });
                    }
                    return null;
                }
                throw new CommerceApiError(body.code);
            }

            if (mountedRef.current && !controller.signal.aborted) {
                const directive =
                    body.snapshot.presentationDirectives?.find(
                        (candidate) => candidate.autoOpen,
                    ) ?? null;
                setCommerceSlice((current) => ({
                    snapshot: body.snapshot,
                    activePresentation: current.activePresentation ?? directive,
                    loadStatus: "ready",
                    error: null,
                }));
            }
            return body.snapshot;
        } catch (error) {
            if (!controller.signal.aborted && mountedRef.current) {
                setCommerceSlice({
                    loadStatus: "error",
                    error: commerceErrorMessage(error),
                });
            }
            if (!controller.signal.aborted) throw error;
            return null;
        } finally {
            if (snapshotRequestRef.current === controller) {
                snapshotRequestRef.current = null;
            }
        }
    }, [enabled, setCommerceSlice]);

    useEffect(() => {
        if (!enabled || !autoLoad || state.loadStatus !== "idle") return;
        void refresh().catch((error: unknown) => {
            console.error("[useCommerce] snapshot failed", error);
        });
    }, [autoLoad, enabled, refresh, state.loadStatus]);

    const recordPresentation = useCallback(
        async (directive: ApiCommercePresentationDirective) => {
            if (presentationRequestsRef.current.has(directive.presentationId)) {
                return;
            }

            const request = presentationRequest(directive);
            presentationRequestsRef.current.set(
                directive.presentationId,
                request.requestId,
            );
            try {
                const { body } = await retryTransportOnce(() =>
                    authenticatedApiCall(
                        "/api/v1/commerce/presentations",
                        ApiCommercePresentationCreateResSchema,
                        {
                            method: "POST",
                            body: JSON.stringify(request),
                        },
                    ),
                );
                if (body.status === "ERROR") {
                    throw new CommerceApiError(body.code);
                }
            } catch (error) {
                presentationRequestsRef.current.delete(
                    directive.presentationId,
                );
                console.error(
                    "[useCommerce] presentation acknowledgement failed",
                    error,
                );
            }
        },
        [],
    );

    const dismissPresentation = useCallback(
        (presentationId: string) => {
            setCommerceSlice((current) => ({
                activePresentation:
                    current.activePresentation?.presentationId ===
                    presentationId
                        ? null
                        : current.activePresentation,
            }));
        },
        [setCommerceSlice],
    );

    const claimChainStep = useCallback(
        async (journey: ApiCommerceChainJourney) => {
            const request = ApiCommerceChainClaimReqSchema.parse({
                requestId: crypto.randomUUID(),
                journeyId: journey.journeyId,
                expectedJourneyVersion: journey.version,
            });
            setCommerceSlice({ mutationStatus: "claiming", error: null });

            try {
                const { body } = await retryTransportOnce(() =>
                    authenticatedApiCall(
                        "/api/v1/commerce/chains/claim",
                        ApiCommerceChainClaimResSchema,
                        {
                            method: "POST",
                            body: JSON.stringify(request),
                        },
                    ),
                );
                if (body.status === "ERROR") {
                    throw new CommerceApiError(body.code);
                }
                if (mountedRef.current) {
                    setCommerceSlice({
                        snapshot: body.snapshot,
                        mutationStatus: "idle",
                        error: null,
                    });
                    setSlotMachineSlice((current) => ({
                        refreshRequestedRevision:
                            current.refreshRequestedRevision + 1,
                    }));
                }
                return body.snapshot;
            } catch (error) {
                if (mountedRef.current) {
                    setCommerceSlice({
                        mutationStatus: "idle",
                        error: commerceErrorMessage(error),
                    });
                }
                throw error;
            }
        },
        [setCommerceSlice, setSlotMachineSlice],
    );

    const purchaseOffer = useCallback(
        async (
            offer: CheckoutOffer,
            chainJourneyId?: string,
            onFulfilled?: (order: ApiCommerceOrder) => void,
            surface?: "BEACH_COIN_OFFER" | "BUNDLE_OFFER",
            standalone?: StandaloneCheckoutContext,
        ): Promise<TelegramInvoiceStatus> => {
            const price = telegramStarsPrice(offer);
            const termsVersion =
                standalone?.termsVersion ?? state.snapshot?.termsVersion;
            if (!enabled || checkoutRequestRef.current) return "pending";
            if (!price || !termsVersion) {
                const error = new CommerceApiError("PRICE_UNAVAILABLE");
                setCommerceSlice({ error: error.message });
                throw error;
            }

            const controller = new AbortController();
            checkoutRequestRef.current = controller;
            preloadSounds(COMMERCE_REWARD_SOUNDS);
            const request = ApiCommerceOrderCreateReqSchema.parse({
                requestId: crypto.randomUUID(),
                offerRevisionId: offer.offerRevisionId,
                priceId: price.priceId,
                ...(chainJourneyId ? { chainJourneyId } : {}),
                termsVersion,
            });
            const analyticsProperties = {
                request_id: request.requestId,
                offer_id: offer.offerId,
                offer_code: offer.offerCode,
                offer_revision_id: offer.offerRevisionId,
                offer_version: offer.version,
                price_id: price.priceId,
                price_xtr: price.unitAmount,
                surface:
                    surface ??
                    (chainJourneyId
                        ? ("CHAIN_OFFER" as const)
                        : ("ENERGY_DRAWER" as const)),
                ...(chainJourneyId ? { chain_journey_id: chainJourneyId } : {}),
                ...(offer.experiment
                    ? {
                          experiment_decision_id: offer.experiment.decisionId,
                          experiment_key: offer.experiment.experimentKey,
                          experiment_variant: offer.experiment.variantKey,
                      }
                    : {}),
            };
            captureClientAnalytics(
                posthog,
                "commerce_checkout_started",
                analyticsProperties,
            );
            setCommerceSlice({
                mutationStatus: "checking-out",
                checkoutStatus: "opening",
                error: null,
            });

            let failureStage:
                "ORDER_CREATE" | "INVOICE_OPEN" | "SETTLEMENT" | "REFRESH" =
                "ORDER_CREATE";
            let analyticsOrderId: string | undefined;
            try {
                const { body } = await retryTransportOnce(() =>
                    authenticatedApiCall(
                        "/api/v1/commerce/orders",
                        ApiCommerceOrderCreateResSchema,
                        {
                            method: "POST",
                            body: JSON.stringify(request),
                            signal: controller.signal,
                        },
                    ),
                );
                if (body.status === "ERROR") {
                    throw new CommerceApiError(body.code);
                }

                controller.signal.throwIfAborted();
                let order = body.order;
                analyticsOrderId = order.orderId;
                let orderAction = commerceOrderClientAction(order.status);
                let invoiceStatus: TelegramInvoiceStatus = "pending";

                if (orderAction === "OPEN_INVOICE") {
                    failureStage = "INVOICE_OPEN";
                    invoiceStatus = await openTelegramInvoice(
                        body.checkout.invoiceLink,
                        () => {
                            captureClientAnalytics(
                                posthog,
                                "commerce_invoice_opened",
                                {
                                    ...analyticsProperties,
                                    order_id: order.orderId,
                                },
                            );
                        },
                    );
                    captureClientAnalytics(posthog, "commerce_invoice_closed", {
                        ...analyticsProperties,
                        order_id: order.orderId,
                        invoice_status: invoiceStatus,
                    });
                    if (!mountedRef.current || controller.signal.aborted)
                        return invoiceStatus;
                    if (!shouldPollAfterInvoiceStatus(invoiceStatus)) {
                        setCommerceSlice({
                            mutationStatus: "idle",
                            checkoutStatus: invoiceStatus,
                        });
                        if (standalone) await standalone.refresh();
                        return invoiceStatus;
                    }
                    setCommerceSlice({
                        checkoutStatus:
                            invoiceStatus === "paid" ? "paid" : "pending",
                    });
                } else if (orderAction === "SUCCEEDED") {
                    invoiceStatus = "paid";
                    setCommerceSlice({ checkoutStatus: "paid" });
                } else if (orderAction === "FAILED") {
                    throw new CommerceApiError("CHECKOUT_FAILED");
                } else {
                    setCommerceSlice({
                        checkoutStatus:
                            order.status === "PAID" ||
                            order.status === "FULFILLING"
                                ? "paid"
                                : "pending",
                    });
                }

                if (
                    orderAction === "WAIT_FOR_SETTLEMENT" ||
                    (orderAction === "OPEN_INVOICE" &&
                        shouldPollAfterInvoiceStatus(invoiceStatus))
                ) {
                    failureStage = "SETTLEMENT";
                    order = await waitForOrderSettlement(
                        order.orderId,
                        controller.signal,
                    );
                    orderAction = commerceOrderClientAction(order.status);
                }

                controller.signal.throwIfAborted();
                if (orderAction === "FAILED") {
                    throw new CommerceApiError("CHECKOUT_FAILED");
                }

                if (orderAction === "SUCCEEDED") {
                    invoiceStatus = "paid";
                    setCommerceSlice({ checkoutStatus: "paid" });
                    if (mountedRef.current && !controller.signal.aborted) {
                        const userId = authState.userId;
                        if (userId) {
                            setCommerceSlice((current) => ({
                                rewardCollection: enqueueCommerceRewardCollection(
                                    current.rewardCollection,
                                    userId,
                                    order,
                                    offer,
                                ),
                            }));
                        }
                        onFulfilled?.(order);
                    }
                } else if (commerceOrderPaymentWasAccepted(order.status)) {
                    invoiceStatus = "paid";
                    setCommerceSlice({ checkoutStatus: "paid" });
                } else {
                    invoiceStatus = "pending";
                    // Settlement can outlive this foreground polling window. Keep
                    // the server order resumable, but release the local checkout
                    // lock so a retry re-reads that same order instead of leaving
                    // the purchase UI permanently disabled.
                    setCommerceSlice({ checkoutStatus: "idle" });
                }

                failureStage = "REFRESH";
                if (
                    orderAction === "SUCCEEDED" &&
                    mountedRef.current &&
                    !controller.signal.aborted
                ) {
                    // An older resumed revision may have a different reward mix.
                    const unknownRewardMix =
                        standalone &&
                        order.offerRevisionId !== offer.offerRevisionId;
                    // Invalidate only domains affected by these authoritative rewards.
                    // Do this before the catalog read so its failure cannot hide fulfillment.
                    if (
                        !standalone ||
                        unknownRewardMix ||
                        offer.rewards.some(
                            (reward) =>
                                reward.kind === "CURRENCY" ||
                                reward.kind === "SM_ENERGY",
                        )
                    ) {
                        setSlotMachineSlice((current) => ({
                            refreshRequestedRevision:
                                current.refreshRequestedRevision + 1,
                        }));
                    }
                    if (
                        standalone &&
                        (unknownRewardMix ||
                            offer.rewards.some((reward) =>
                                reward.kind.startsWith("ITEM_"),
                            ))
                    ) {
                        setGnomeSlice((current) => ({
                            refreshRequestedRevision:
                                current.refreshRequestedRevision + 1,
                        }));
                    }
                    if (
                        standalone &&
                        (unknownRewardMix ||
                            offer.rewards.some(
                                (reward) => reward.kind === "WHEEL_SPINS",
                            ))
                    ) {
                        setWheelSlice((current) => ({
                            refreshRequestedRevision:
                                current.refreshRequestedRevision + 1,
                        }));
                    }
                }
                await (standalone ? standalone.refresh() : refresh());
                controller.signal.throwIfAborted();

                if (mountedRef.current) {
                    setCommerceSlice({ mutationStatus: "idle" });
                }
                return invoiceStatus;
            } catch (error) {
                if (!controller.signal.aborted) {
                    captureClientAnalytics(
                        posthog,
                        "commerce_checkout_failed",
                        {
                            ...analyticsProperties,
                            ...(analyticsOrderId
                                ? { order_id: analyticsOrderId }
                                : {}),
                            stage: failureStage,
                            reason_code:
                                error instanceof CommerceApiError
                                    ? error.code
                                    : "UNEXPECTED_ERROR",
                        },
                    );
                }
                if (!controller.signal.aborted && mountedRef.current) {
                    setCommerceSlice({
                        mutationStatus: "idle",
                        checkoutStatus: "failed",
                        error: commerceErrorMessage(error),
                    });
                }
                throw error;
            } finally {
                if (checkoutRequestRef.current === controller) {
                    checkoutRequestRef.current = null;
                }
            }
        },
        [
            enabled,
            posthog,
            refresh,
            setCommerceSlice,
            setSlotMachineSlice,
            setGnomeSlice,
            setWheelSlice,
            state.snapshot,
            authState.userId,
        ],
    );

    const resetCheckout = useCallback(() => {
        setCommerceSlice({ checkoutStatus: "idle", error: null });
    }, [setCommerceSlice]);

    return {
        snapshot: state.snapshot,
        activePresentation: state.activePresentation,
        loadStatus: state.loadStatus,
        mutationStatus: state.mutationStatus,
        checkoutStatus: state.checkoutStatus,
        error: state.error,
        isCollectingRewards:
            state.rewardCollection?.userId === authState.userId &&
            state.rewardCollection.queue.length > 0,
        refresh,
        recordPresentation,
        dismissPresentation,
        claimChainStep,
        purchaseOffer,
        resetCheckout,
    };
}

export type CommerceController = ReturnType<typeof useCommerce>;

export function activeChainJourney(
    snapshot: ApiCommerceSnapshot | null,
): ApiCommerceChainJourney | null {
    if (!snapshot) return null;
    return (
        snapshot.chainJourneys.find(
            (journey) =>
                journey.status === "ACTIVE" &&
                Date.parse(journey.expiresAt) > Date.now(),
        ) ?? null
    );
}
